import { useCallback } from 'react';
import { useLogger } from './Entities';
import { PrintRow } from '~Components/PrinterManager';

/**
 * useTextToImage hook
 *
 * Takes an array of strings with size, position and content and returns a
 * base64 string representation of the image.
 *
 * Each string is appended after each other, so the order of the array is
 * important.
 */
export const useTextToImage = () => {
  const mapFontSize = (size?: number) => {
    const x = 1.4;
    return Math.round(size ? size * x : 16 * x);
  };

  const logger = useLogger('text-to-image');

  return useCallback((rows: PrintRow[]) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      logger.error('Could not get context');
      return '';
    }

    let height = 100;

    context.imageSmoothingEnabled = false;

    canvas.width = 380;
    canvas.height = rows.reduce((acc, row) => acc + mapFontSize(row.fontSize) + 4, 0);

    context.fillStyle = '#fff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    context.fillStyle = '#000';

    rows.forEach((row, i) => {
      const fontSize = mapFontSize(row.fontSize);
      const text = row.content.trim();

      let xPos = 0;
      context.textAlign = 'left';
      switch (row.alignment) {
        case 'CENTER':
          context.textAlign = 'center';
          xPos = Math.round(canvas.width / 2);
          break;
        case 'RIGHT':
          xPos = canvas.width - context.measureText(text).width;
          break;
        default:
          xPos = 0;
          break;
      }

      // ${row.bold ? 'bold' : 'normal'}
      context.font = `bold ${fontSize}px Monospace`;

      context.fillText(text, xPos, height);

      height += fontSize + 3;
    });

    const data = canvas.toDataURL('image/png').replace('data:image/png;base64,', '');

    logger.info('Text-to-Image generated', { data, rows });

    return data!;
  }, []);
};
